'use client';
import { Input, chakra } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import { CustomMedicationDto } from '@/lib/ServerApi';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { DialogBody, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Field, FieldGroup, FieldRow } from '@/components/ui/field';
import { NumberInputField, NumberInputRoot } from '@/components/ui/number-input';
import { toaster } from '@/components/ui/toaster';

const createMedicationSchema = z.object({
    name: z.string().min(1, 'required'),
    dose: z.string().min(1, 'required'),
    doseUnit: z.string().min(1, 'required'),
    route: z.string().min(1, 'required'),
});

type CreateMedicationFormValues = z.infer<typeof createMedicationSchema>;

interface CreateMedicationFormProps {
    initialName?: string;
    onBack: () => void;
    onCreated: (medication: CustomMedicationDto) => void;
}

export const CreateMedicationForm = ({ initialName, onBack, onCreated }: CreateMedicationFormProps) => {
    const {
        control,
        register,
        handleSubmit,
        formState: { errors },
    } = useForm<CreateMedicationFormValues>({
        defaultValues: {
            name: initialName ?? '',
            dose: '',
            doseUnit: '',
            route: '',
        },
        resolver: zodResolver(createMedicationSchema),
        mode: 'onSubmit',
        reValidateMode: 'onChange',
    });

    const { mutate: createMedication, isPending } = useMutation({
        mutationFn: (values: CreateMedicationFormValues) => {
            return api.customMedication.create(values).then((r) => r.data);
        },
        onSuccess: (medication) => {
            onCreated(medication);
        },
        onError: () => {
            toaster.create({
                title: 'Failed to create medication',
                type: 'error',
                duration: 3000,
            });
        },
    });

    return (
        <chakra.form
            noValidate
            display="flex"
            flex="1"
            flexDir="column"
            overflow="auto"
            onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                return handleSubmit((values) => createMedication(values))(e);
            }}>
            <DialogHeader>
                <DialogTitle>Create New Medication</DialogTitle>
            </DialogHeader>
            <DialogBody>
                <FieldGroup>
                    <FieldRow>
                        <Field label="Medication Name" required invalid={!!errors.name} errorText={errors.name?.message}>
                            <Input type="text" {...register('name')} autoFocus />
                        </Field>
                    </FieldRow>
                    <FieldRow>
                        <Field label="Dose" required invalid={!!errors.dose} errorText={errors.dose?.message} flex="0 0 120px">
                            <Controller
                                name="dose"
                                control={control}
                                render={({ field }) => (
                                    <NumberInputRoot
                                        size="sm"
                                        min={0}
                                        name={field.name}
                                        value={`${field.value}`}
                                        onValueChange={({ value }) => field.onChange(value)}>
                                        <NumberInputField onBlur={field.onBlur} bgColor="#FAFAFA" borderColor="#D6D6D6" />
                                    </NumberInputRoot>
                                )}
                            />
                        </Field>
                        <Field label="Unit" required invalid={!!errors.doseUnit} errorText={errors.doseUnit?.message} flex="0 0 120px">
                            <Controller control={control} name="doseUnit" render={({ field }) => <Input type="text" {...field} />} />
                        </Field>
                        <Field label="Route" required invalid={!!errors.route} errorText={errors.route?.message}>
                            <Controller control={control} name="route" render={({ field }) => <Input type="text" {...field} />} />
                        </Field>
                    </FieldRow>
                </FieldGroup>
            </DialogBody>
            <DialogFooter>
                <Button type="button" variant="outline" onClick={onBack}>
                    Cancel
                </Button>
                <Button type="submit" loading={isPending} loadingText="Adding">
                    Add Medication
                </Button>
            </DialogFooter>
        </chakra.form>
    );
};
